/**
 * Security form-effect — handles form closure, honeypot field styling,
 * and password protection markers.
 * SSR-safe: returns undefined if document is not available.
 */

import type { FormEffectHandler } from '@atlas-forms/types-js';
import { str } from './utils';

export const securityEffect: FormEffectHandler = (config) => {
  if (typeof document === 'undefined') return;

  const created: Element[] = [];

  // Closed-after-date check
  const closedAfterDate = str(config.closedAfterDate);
  if (closedAfterDate) {
    const closeDate = new Date(closedAfterDate);
    if (!isNaN(closeDate.getTime()) && new Date() > closeDate) {
      const alertDiv = document.createElement('div');
      alertDiv.setAttribute('role', 'alert');
      alertDiv.style.cssText =
        'text-align:center;padding:32px;font-family:sans-serif;color:#6b7280;';
      alertDiv.textContent = 'This form is no longer accepting responses.';
      const formEl = document.querySelector('.af-form-renderer');
      if (formEl) {
        formEl.prepend(alertDiv);
      } else {
        document.body.prepend(alertDiv);
      }
      created.push(alertDiv);
      document.body.setAttribute('data-atlas-form-closed', 'true');
    }
  }

  // Honeypot field styles
  if (config.honeypotEnabled === true) {
    const style = document.createElement('style');
    style.textContent =
      '.af-honeypot-field { position:absolute;left:-9999px;top:-9999px;opacity:0;pointer-events:none;tab-index:-1; }';
    document.head.appendChild(style);
    created.push(style);
  }

  // Password protection marker
  if (config.passwordProtected === true) {
    document.body.setAttribute('data-atlas-password-required', 'true');
  }

  return () => {
    for (const el of created) {
      el.parentNode?.removeChild(el);
    }
    document.body.removeAttribute('data-atlas-form-closed');
    document.body.removeAttribute('data-atlas-password-required');
  };
};
